import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ExternalCallOperationStat } from "@/features/admin";
import { groupLabel, ms, pct } from "./external-calls-format";

/**
 * Which endpoints are called, and how often — the answer to "500 Provalliance calls
 * yesterday, what are they?". Shown with or without a selected integration: across all of
 * them it is where one finds the endpoint the traffic goes to, inside one it is the
 * drill-down from "Provalliance is slow" to "`POST /visits` is slow, the reads are fine".
 *
 * Ids in the paths are collapsed on the server (`/visits/:id`), so a row is an endpoint,
 * not an appointment. The share column is against the calls of the table itself, so it
 * reads as "how much of this traffic".
 */
export function ExternalCallOperations({
  operations,
  group,
}: {
  operations: ExternalCallOperationStat[];
  group?: string;
}) {
  const total = operations.reduce((sum, row) => sum + row.calls, 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Par opération — {group ? groupLabel(group) : "toutes intégrations"}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Les identifiants sont remplacés dans les chemins ({"/visits/:id"},{" "}
          {"/days/:date"}) pour que les appels d'un même endpoint restent
          regroupés. Les paramètres de requête ne sont jamais enregistrés.
        </p>
      </CardHeader>
      <CardContent>
        {operations.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune donnée.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Opération</TableHead>
                {group ? null : <TableHead>Intégration</TableHead>}
                <TableHead>Cible</TableHead>
                <TableHead className="text-right">Appels</TableHead>
                <TableHead className="text-right">Part</TableHead>
                <TableHead className="text-right">Clients</TableHead>
                <TableHead className="text-right">Erreurs</TableHead>
                <TableHead className="text-right">p50</TableHead>
                <TableHead className="text-right">p95</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operations.map((row) => (
                <TableRow
                  key={`${row.api_group}:${row.target}:${row.method}:${row.operation}`}
                >
                  <TableCell className="font-mono text-xs">
                    <span className="text-muted-foreground mr-2">
                      {row.method}
                    </span>
                    {row.operation}
                  </TableCell>
                  {group ? null : (
                    <TableCell>{groupLabel(row.api_group)}</TableCell>
                  )}
                  <TableCell className="text-muted-foreground">
                    {row.target}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.calls.toLocaleString("fr-FR")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {total ? pct(row.calls / total) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.clients}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      row.errors ? "text-red-600 dark:text-red-400" : ""
                    }`}
                  >
                    {row.errors ? pct(row.error_rate) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ms(row.p50_ms)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ms(row.p95_ms)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
